/**
 * Minimal zod ↔ react-hook-form bridge.
 *
 * `@hookform/resolvers` pulls in optional peers for half a dozen validation
 * libraries we do not use, so this 25-line adapter replaces it. It covers what
 * the forms here need: flat and one-level-nested field errors.
 */

import type { FieldValues, Resolver } from 'react-hook-form';
import type { ZodType } from 'zod';

export function zodResolver<TSchema extends ZodType, TValues extends FieldValues>(
  schema: TSchema,
): Resolver<TValues> {
  return async (values) => {
    const result = schema.safeParse(values);

    if (result.success) {
      return { values: result.data as TValues, errors: {} };
    }

    const errors: Record<string, { type: string; message: string }> = {};
    for (const issue of result.error.issues) {
      const name = issue.path.join('.') || 'root';
      // Keep the first message per field — matches react-hook-form's default.
      errors[name] ??= { type: issue.code, message: issue.message };
    }

    return { values: {} as TValues, errors: errors as never };
  };
}
